import { computed, inject, Injectable, signal } from '@angular/core';
import { ProductRelocationRepository } from '../repositories/product-reloaction.repository';
import { FinalizeProductRelocationBody } from '../types/finialize';
import { isDefinedNotEmpty } from '@softline/core';
import { LagerplatzInhalt } from '@softapps/lager/core';

@Injectable()
export class ProductRelocationService {
  readonly #repository = inject(ProductRelocationRepository);

  readonly loadedPallet = signal<{ code: string; result: LagerplatzInhalt } | undefined>(
    undefined
  );

  readonly #body = signal<Partial<FinalizeProductRelocationBody>>({});

  readonly complete = computed(() => {
    const value = this.#body();
    return this.isValidBody(value);
  });

  readonly productQuantitySet = computed(() => {
    return isDefinedNotEmpty(this.#body()?.artikelMenge);
  });

  update<Key extends keyof FinalizeProductRelocationBody>(
    key: Key,
    value: FinalizeProductRelocationBody[Key]
  ) {
    this.#body.update((currentValue) => ({
      ...currentValue,
      [key]: value,
    }));
  }

  reset(key?: keyof FinalizeProductRelocationBody | undefined) {
    if (key) {
      const currentValue = this.#body();
      delete currentValue[key];
      this.#body.set(currentValue);
    } else {
      this.#body.set({});
    }
  }

  finalize(value: FinalizeProductRelocationBody['zielLagerplatz']) {
    this.#body.set({ ...this.#body(), zielLagerplatz: value });
    const completedBody = this.#body();
    if (!this.isValidBody(completedBody)) return;
    return this.#repository.finalize(completedBody);
  }

  private isValidBody(
    value: Partial<FinalizeProductRelocationBody>
  ): value is FinalizeProductRelocationBody {
    return (
      isDefinedNotEmpty(value?.quellLagerplatz) &&
      isDefinedNotEmpty(value?.zielLagerplatz) &&
      isDefinedNotEmpty(value?.artikelMenge)
    );
  }
}
